import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";

/**
 * Composable used to handle the tabs navigations in a page. This composable is
 * typically used on pages using multiple tabs with dedicated paths via the
 * usage of the `LxcTabs` component.

 * @param tabNavigations Representations of the tabs used in the page.
 */
export function useTabsNavigation({
  tabNavigations,
}: {
  tabNavigations: TabNavigation[];
}) {
  const route = useRoute();
  const router = useRouter();

  /**
   * The selected tab index. The value can be `undefined` if invalid data is
   * provided to the composable. Otherwise, the selected index is either the tab
   * index desired by the user or the first tab in the `tabNavigations`
   * configuration if the index cannot be found.
   */
  const selectedTabIndex = ref(setDefaultSelectedTabIndex(route.path));

  function setDefaultSelectedTabIndex(path: string): number | undefined {
    for (const [index, tabNavigation] of tabNavigations.entries()) {
      if (path.startsWith(tabNavigation.redirectPath)) {
        return index;
      }
    }
    return undefined;
  }

  function setSelectedTabIndex(index: number): number | undefined {
    const selectedTabNavigation = tabNavigations[index];
    if (selectedTabNavigation) {
      return index;
    }
  }

  /**
   * Update the `selectedTabIndex` index and perform the navigation to the tab.
   * If the selected index is not valid the user is automatically redirected to
   * the first tab in the `tabNavigations` configuration.
   * @param index Index of the tab to be displayed to the user.
   */
  async function onTabSelected(index: number) {
    selectedTabIndex.value = setSelectedTabIndex(index);
    if (selectedTabIndex.value !== undefined) {
      const selectedTabNavigation = tabNavigations[index];
      if (selectedTabNavigation) {
        await router.push(selectedTabNavigation.redirectPath);
      }
    }
  }

  /**
   * Handle the redirection to the tab when the route changes
   * buttons.
   */
  watch(route, () => {
    selectedTabIndex.value = setDefaultSelectedTabIndex(route.path);
  });

  return {
    selectedTabIndex,
    onTabSelected,
  };
}
